/**
 * Página de Producto con ReelsToBuy
 * Ruta: /reel/:slug
 *
 * Carga producto desde la API de este sitio y lo muestra con ReelsToBuy.
 * Detecta país automáticamente y redirige a la pasarela correcta.
 */

import { useEffect, useState } from 'react';
import { getCanonicalLocation } from "@/lib/canonicalLocation";
import { getApiBaseUrl } from '@/config/apiUrl';
import { useParams, useNavigate } from 'react-router-dom';
import ReelsToBuy, { MediaItem } from '@/components/ReelsToBuy';
import { getPostHogStripeMetadataPostHog, flushPostHogBeforeNavigation } from '@/lib/posthogAnalytics';
import {
  trackMuxPaywallFailure,
  trackMuxStripeRedirect,
} from '@/lib/muxPaywallFunnelTracking';

interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_usd: number;
  price_ars?: number;
  recommended_price: number;
  recommended_currency: string;
  image_url?: string;
  image_alt?: string;
  hero_image_url?: string;
  thumbnail_url?: string;
  gallery_images?: string[];
  video_url?: string; // Mux Playback ID
  cta_button_text?: string;
  cta_button_color?: string;
  show_quick_buy?: boolean;
  page_title?: string;
  page_description?: string;
  metadata?: Record<string, any>;
  stripe_price_id?: string;
  mercadopago_plan_id?: string;
}

// Helper para detectar país del usuario
async function detectUserCountry(): Promise<string> {
  try {
    // Opción 1: Cloudflare headers (si está disponible)
    // VERDAD ÚNICA: país del Worker (__TEMPLATE_CONFIG__ vía canonicalLocation).
    const canonical = getCanonicalLocation().country;
    if (canonical) return canonical;
    
    // Opción 2: endpoint server-side (headers CF) — nunca proveedores geo del cliente.
    const response = await fetch('/api/detect-country');
    const data = await response.json();
    return data.country && data.country !== 'XX' ? data.country : 'US';
  } catch (error) {
    console.error('Error detecting country:', error);
    return 'US'; // Default
  }
}

// Helper para decidir pasarela de pago
function getPaymentProvider(recommendedCurrency?: string, country?: string): 'stripe' | 'mercadopago' {
  // Regla de negocio principal:
  // - ARS => MercadoPago
  // - USD (u otras) => Stripe
  if ((recommendedCurrency || '').toUpperCase() === 'ARS') {
    return 'mercadopago';
  }
  // Fallback defensivo por país si la moneda no viene definida
  return country === 'AR' ? 'mercadopago' : 'stripe';
}

export default function ProductReel() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProduct() {
      if (!slug) {
        setError('Slug no proporcionado');
        setLoading(false);
        return;
      }

      try {
        // 1. Detectar país del usuario
        const country = await detectUserCountry();
        
        // 2. Obtener producto de la API (mismo origen o VITE_API_URL en dev)
        const apiBase = getApiBaseUrl();
        const response = await fetch(
          `${apiBase}/api/products/by-slug?slug=${slug}&country=${country}`
        );
        
        if (!response.ok) {
          throw new Error('Producto no encontrado');
        }
        
        const { product } = await response.json();
        setProduct(product);
        
        // 3. Crear items para ReelsToBuy
        const reelItems: MediaItem[] = [];
        
        // Prioridad 1: Video de Mux (si existe)
        if (product.video_url) {
          reelItems.push({
            type: "video",
            src: product.video_url, // Mux Playback ID
            thumbnail: product.thumbnail_url || product.image_url
          });
        }
        
        // Prioridad 2: Imagen principal
        if (product.image_url) {
          reelItems.push({
            type: "image",
            src: product.image_url,
            alt: product.image_alt || product.name
          });
        }
        
        // Agregar galería si existe
        if (product.gallery_images && Array.isArray(product.gallery_images)) {
          product.gallery_images.forEach((imageUrl: string) => {
            reelItems.push({
              type: "image",
              src: imageUrl,
              alt: product.image_alt || product.name
            });
          });
        }
        
        // Si no hay nada, usar placeholder
        if (reelItems.length === 0) {
          reelItems.push({
            type: "image",
            src: "https://via.placeholder.com/400x600?text=" + encodeURIComponent(product.name),
            alt: product.name
          });
        }
        
        setItems(reelItems);
        
        // 4. Actualizar meta tags para SEO
        if (product.page_title) {
          document.title = product.page_title;
        }
        
        if (product.page_description) {
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', product.page_description);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading product:', err);
        trackMuxPaywallFailure({
          surface: 'product_reel',
          productSlug: slug,
          failureType: 'price_load_failed',
          failureReason: err instanceof Error ? err.message : 'product_load_error',
          componentName: 'ProductReel',
        });
        setError(err instanceof Error ? err.message : 'Error al cargar producto');
        setLoading(false);
      }
    }
    
    loadProduct();
  }, [slug]);

  useEffect(() => {
    if (loading || error || !product || items.length > 0) return;
    trackMuxPaywallFailure({
      surface: 'product_reel',
      productSlug: product.slug,
      productId: product.id,
      failureType: 'component_missing',
      failureReason: 'product_reel_zero_items',
      componentName: 'ProductReel',
    });
  }, [loading, error, product, items.length]);

  // Función de compra
  const handlePurchase = async () => {
    if (!product) return;

    try {
      // 1. Detectar país
      const country = await detectUserCountry();
      
      // 2. Decidir pasarela según moneda recomendada (ARS -> MP, USD -> Stripe)
      const provider = getPaymentProvider(product.recommended_currency, country);
      
      console.log(`País detectado: ${country}, Pasarela: ${provider}`);
      
      // 3. Redirigir según la pasarela
      if (provider === 'mercadopago') {
        // Crear preferencia de MercadoPago
        const response = await fetch('/api/mercadopago/create-preference', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: product.id,
            slug: product.slug,
            productName: product.name,
            price: product.recommended_price,
            currency: product.recommended_currency,
            country,
            // Atribución + linkage PostHog → Slack sale_confirmed del webhook MP
            metadata: getPostHogStripeMetadataPostHog(),
          })
        });
        
        if (!response.ok) {
          throw new Error('Error al crear preferencia de MercadoPago');
        }
        
        const { init_point } = await response.json();
        trackMuxStripeRedirect({
          surface: 'product_reel',
          gateway: 'mercadopago',
          productId: product.id,
          productSlug: product.slug,
          packName: product.name,
          priceShown: `${product.recommended_currency} ${product.recommended_price}`,
          priceAmount: product.recommended_price,
          priceCurrency: product.recommended_currency,
          playbackId: product.video_url,
          redirectUrl: init_point,
        });
        window.location.href = init_point;
      } else {
        // Stripe custom checkout: soporta priceId o precio dinámico
        const amountCents = Math.round(Number(product.recommended_price) * 100);
        const origin = window.location.origin;

        await flushPostHogBeforeNavigation(350);

        const response = await fetch('/api/create-subscription-checkout?redirect=1', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Checkout-Redirect': '1',
          },
          body: JSON.stringify({
            amount: amountCents,
            productName: product.name,
            successUrl: `${origin}/muchas-gracias`,
            cancelUrl: `${origin}/p/${product.slug}`,
            metadata: {
              plan_tier: product.plan_tier || 'standard',
              flow: 'product_reel',
              product_id: product.id,
              country,
              ...getPostHogStripeMetadataPostHog(),
            },
          }),
          redirect: 'manual',
        });
        
        const redirectUrl =
          response.headers.get('Location') ||
          (response.ok ? (await response.json().catch(() => ({}))).url : null);

        if (redirectUrl) {
          trackMuxStripeRedirect({
            surface: 'product_reel',
            gateway: 'stripe',
            productId: product.id,
            productSlug: product.slug,
            packName: product.name,
            priceShown: `${product.recommended_currency} ${product.recommended_price}`,
            priceAmount: product.recommended_price,
            priceCurrency: product.recommended_currency,
            playbackId: product.video_url,
            redirectUrl,
          });
          window.location.href = redirectUrl;
        } else if (!response.ok) {
          throw new Error('Error al crear sesión de Stripe');
        } else {
          navigate(`/buy/${slug}`);
        }
      }
    } catch (error) {
      console.error('Error during purchase:', error);
      trackMuxPaywallFailure({
        surface: 'product_reel',
        productId: product.id,
        productSlug: product.slug,
        failureType: 'checkout_api_error',
        failureReason: error instanceof Error ? error.message : 'checkout_error',
        priceShown: `${product.recommended_currency} ${product.recommended_price}`,
        priceAmount: product.recommended_price,
        priceCurrency: product.recommended_currency,
      });
      alert('Error al procesar la compra. Por favor intenta de nuevo.');
    }
  };

  // Estados de carga y error
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center text-white px-4">
          <h1 className="text-2xl font-bold mb-4">Producto no encontrado</h1>
          <p className="mb-6">{error || 'El producto que buscas no existe'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-white text-black rounded-lg font-semibold"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white text-xl">No hay imágenes disponibles</div>
      </div>
    );
  }

  return (
    <ReelsToBuy
      items={items}
      maxFreeItems={3}
      price={`${product.recommended_currency} ${product.recommended_price}`}
      packName={product.name}
      buttonText={product.cta_button_text || 'Comprar Ahora'}
      buttonColor={product.cta_button_color || '#ec4899'}
      heroImage={product.hero_image_url}
      description={product.page_description || product.description}
      analyticsSurface="product_reel"
      productId={product.id}
      productSlug={product.slug}
      priceAmount={product.recommended_price}
      priceCurrency={product.recommended_currency}
      onPurchaseClick={handlePurchase}
    />
  );
}
